import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';

import IGlassArea from 'components/interfaces/IGlassArea';
import IGlassWallDoor from 'components/interfaces/IGlassWallDoor';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';
import { setWallMetaData } from 'store/wallsMetaData/actions';

import {
  setPreviousWall,
  setWallMetaData as setSelectedWallMetaData,
} from 'store/selectedWall/actions';

import IWallMetaData from 'components/interfaces/IWallMetaData';
import IWall from 'components/interfaces/IWall';
import { RootState } from 'store/store';
import copyWall from '../propertiesBar/services/copyWall';
import doWallPreSaveActions from './src/glassWall/doWallPreSaveActions';
import getAmountOfGlassAreas from '../services/getAmountOfGlassAreas';
import getDefaultGlassArea from './src/glassWall/glassAreas/getDefaultGlassArea';
import getDefaultGlassAreaMetaData from '../services/getDefaultGlassAreaMetaData';
import getDoorsFromWallParts from '../services/getDoorsFromWallParts';
import DraggableModal from './src/DraggableModal';
import IValidationError from './src/glassWall/IValidationError';
import placeGlasses from './src/glassWall/placeGlasses';
import Tabs from './src/glassWall/Tabs';
import tabTypes from './src/glassWall/tabTypes';
import validateGlassWall from './src/glassWall/validateGlassWall';
import WoodyWallPreviewModal from './WoodyWallPreviewModal';

import 'style/layout/Forms.css';
import validateWoodyWall from './src/glassWall/validateWoodyWall';

const GlassWallModal = ({
  closeModalNotSaved,
  glassAreas,
  hasWritePermissions,
  restartFields,
  setGlassAreas,
  wallCoordinates,
}: {
  closeModalNotSaved: () => void;
  glassAreas: IGlassArea[];
  hasWritePermissions: boolean;
  restartFields: () => void;
  setGlassAreas: (areas: IGlassArea[]) => void;
  wallCoordinates: any;
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const wall = useSelector(
    (state: RootState) => state.selectedWall.properties
  ) as IWall;
  const wallMetaData = useSelector(
    (state: RootState) => state.selectedWall.metaData
  ) as IWallMetaData;
  const { t } = useTranslation([translations.common]);

  const [selectedTab, setSelectedTab] = useState(tabTypes.wall);
  const [validationErrors, setValidationErrors] = useState<IValidationError[]>(
    []
  );

  const [doors, setDoors] = useState<IGlassWallDoor[]>([]);

  const [woodyWallPreviewsOffset, setWoodyWallPreviewsOffset] = useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });

  useEffect(() => {
    setValidationErrors([
      ...validateGlassWall(wall, wallMetaData, glassAreas),
      ...validateWoodyWall(wall),
    ]);
  }, [wall, glassAreas, wallMetaData]);

  useEffect(() => {
    setDoors(getDoorsFromWallParts(wall));
  }, [wall.parts]);

  useEffect(() => {
    if (doors.length >= glassAreas.length) {
      const newGlassAreas = [...glassAreas];
      while (doors.length >= newGlassAreas.length) {
        newGlassAreas.push(getDefaultGlassArea());
      }

      setGlassAreas(newGlassAreas);
    }

    if (doors.length >= wallMetaData.glassAreas.length) {
      const newWallMetaDataGlassAreas = [...wallMetaData.glassAreas];

      while (doors.length >= newWallMetaDataGlassAreas.length) {
        newWallMetaDataGlassAreas.push(getDefaultGlassAreaMetaData());
      }

      dispatch(
        setSelectedWallMetaData({
          ...wallMetaData,
          glassAreas: newWallMetaDataGlassAreas,
        })
      );
    }
  }, [doors]);

  const editGlass = () => {
    if (validationErrors.length > 0) {
      dispatch(addMessage(`Saving failed!\n${validationErrors[0].message}`));

      return;
    }

    window.DisablePreviewSplits(false);

    const amountOfGlassAreas = getAmountOfGlassAreas(
      getDoorsFromWallParts(wall),
      wall
    );

    dispatch(
      setWallMetaData({
        ...wallMetaData,
        glassAreas: wallMetaData.glassAreas.slice(0, amountOfGlassAreas),
        placed: true,
      })
    );

    dispatch(setPreviousWall(copyWall(wall)));

    const parts = placeGlasses(wall, glassAreas);
    window.UpdateGlassWall(doWallPreSaveActions(wall), parts);

    restartFields();
  };

  return (
    <>
      <DraggableModal
        closeModalNotSaved={closeModalNotSaved}
        wallCoordinates={wallCoordinates}
        id="glass-wall-modal"
        onDrag={(_, data) => {
          setWoodyWallPreviewsOffset(data);
        }}
      >
        <Tabs
          doors={doors}
          glassAreas={glassAreas}
          selectedTab={selectedTab}
          setGlassAreas={setGlassAreas}
          setSelectedTab={setSelectedTab}
          validationErrors={validationErrors}
          hasWritePermissions={hasWritePermissions}
          wall={wall}
          wallMetaData={wallMetaData}
        />
        <Row>
          <Col>
            <PrimaryButton onClick={editGlass} className="button-edit-popup">
              {t(
                `${translations.common}:Wall Actions.Edit Wall`
              ).toLocaleUpperCase()}
            </PrimaryButton>
          </Col>
        </Row>
      </DraggableModal>

      {selectedTab === tabTypes.woody && (
        <WoodyWallPreviewModal offset={woodyWallPreviewsOffset} wall={wall} />
      )}
    </>
  );
};

export default GlassWallModal;
